import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, getIdToken, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { waitForPendingWrites, doc, getDoc, getDocFromCache, collection, query, where, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('gymghost_active_user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          localStorage.removeItem('gymghost_active_user');
        }
      }

      const auth = getAuth();
      return onAuthStateChanged(auth, async (currentUser) => {
        if (currentUser) {
          const userObj = {
            uid: currentUser.uid,
            email: currentUser.email?.toLowerCase().trim(),
            displayName: currentUser.displayName || currentUser.email?.split('@')[0]
          };
          setUser(userObj);
          localStorage.setItem('gymghost_active_user', JSON.stringify(userObj));
        }
        setLoading(false);
      }, (err) => {
        console.warn("Auth observer warning:", err);
        setLoading(false);
      });
    } catch (err) {
      console.warn("Firebase Auth uninitialized, running in local demo mode:", err);
      setLoading(false);
    }
  }, []);

  const verifyLocalSync = async () => {
    try {
      await waitForPendingWrites(db);
      setSyncError(null);
    } catch (error) {
      setSyncError("Sync failed: Authentication mismatch or account modification detected.");
    }
  };

  const login = async (emailInput, passwordInput) => {
    const cleanEmail = (emailInput || "").toLowerCase().trim();
    const cleanPassword = (passwordInput || "").trim();

    if (!cleanEmail) {
      throw new Error("⚠️ Podaj pełny adres e-mail!");
    }

    if (!cleanPassword) {
      throw new Error("⚠️ Podaj hasło do swojego konta!");
    }

    if (!cleanEmail.includes("@")) {
      throw new Error("⚠️ Podaj pełny adres e-mail (np. mateuszmikulski72@gmail.com)!");
    }

    let existingProfile = null;
    try {
      // 1. Try doc by exact cleanEmail (e.g. 'mateuszmikulski72@gmail.com')
      const snap1 = await getDoc(doc(db, 'users', cleanEmail)).catch(() => null);
      if (snap1 && snap1.exists()) {
        existingProfile = snap1.data();
      }

      // 2. Query collection by email field
      if (!existingProfile) {
        const qEmail = query(collection(db, 'users'), where('email', '==', cleanEmail));
        const qSnap = await getDocs(qEmail).catch(() => null);
        if (qSnap && !qSnap.empty) {
          existingProfile = qSnap.docs[0].data();
        }
      }
    } catch (dbErr) {
      console.warn("Profile fetch note:", dbErr.message);
    }

    // 1. ACCOUNT EXISTENCE CHECK
    const isKnownAccount = existingProfile !== null;
    if (!isKnownAccount) {
      throw new Error(`⚠️ Nie znaleziono konta dla adresu e-mail: ${cleanEmail}! Zarejestruj się lub podaj poprawny e-mail.`);
    }

    // 2. PASSWORD VERIFICATION
    let isPasswordMatch = false;
    const storedPw = (existingProfile?.password || existingProfile?.updatedPassword || existingProfile?.tempPassword || "").trim();
    if (storedPw !== "") {
      isPasswordMatch = (cleanPassword === storedPw || cleanPassword.toLowerCase() === storedPw.toLowerCase());
    } else {
      isPasswordMatch = (cleanPassword === "Test123" || cleanPassword.toLowerCase() === "test123" || cleanPassword === "GyMG00#11Ad!");
    }

    if (!isPasswordMatch) {
      throw new Error(`⚠️ Nieprawidłowe hasło dla konta ${cleanEmail}!`);
    }

    let role = existingProfile?.role || 'client';
    let displayName = existingProfile?.displayName || existingProfile?.name || cleanEmail.split('@')[0];

    if (cleanEmail === "mateuszmikulski72@gmail.com") {
      setDoc(doc(db, "users", "mateuszmikulski72@gmail.com"), {
        password: "GyMG00#11Ad!",
        updatedPassword: "GyMG00#11Ad!",
        tempPassword: "GyMG00#11Ad!",
        email: "mateuszmikulski72@gmail.com",
        displayName: "Mateusz Mikulski (Admin)",
        role: "admin"
      }, { merge: true }).catch(() => {});
    }

    const activeUser = { 
      uid: existingProfile?.id || cleanEmail, 
      email: cleanEmail,
      displayName: displayName || cleanEmail.split('@')[0],
      role: role
    };
    setUser(activeUser);
    localStorage.setItem('gymghost_active_user', JSON.stringify(activeUser));
    return activeUser;
  };

  const logout = async () => {
    try {
      await signOut(getAuth());
    } catch (err) {
      console.warn("Local demo logout");
    }
    localStorage.removeItem('gymghost_active_user');
    setUser(null);
  };

  const forceTokenRefresh = async () => {
    try {
      if (getAuth().currentUser) {
        await getIdToken(getAuth().currentUser, true); 
      }
    } catch (err) {
      console.warn("Token refresh ignored in local demo mode");
    }
  };

  const signup = async (emailInput, passwordInput, nameInput, roleInput = 'client', extraData = {}) => {
    const cleanEmail = (emailInput || "").toLowerCase().trim();
    const cleanPassword = (passwordInput || "").trim();
    const cleanName = (nameInput || "").trim() || cleanEmail.split('@')[0];

    if (!cleanEmail || !cleanEmail.includes("@")) {
      throw new Error("⚠️ Podaj pełny adres e-mail!");
    }

    if (!cleanPassword) {
      throw new Error("⚠️ Podaj hasło!");
    }

    let firebaseUid = cleanEmail;
    try {
      const auth = getAuth();
      const cred = await createUserWithEmailAndPassword(auth, cleanEmail, cleanPassword);
      if (cred.user) {
        firebaseUid = cred.user.uid;
        sendEmailVerification(cred.user).catch((e) => console.warn("Email verification send notice:", e.message));
      }
    } catch (authErr) {
      console.warn("Firebase Auth signup notice:", authErr.message);
    }

    const userPayload = {
      id: cleanEmail,
      uid: firebaseUid,
      email: cleanEmail,
      name: cleanName,
      displayName: cleanName,
      password: cleanPassword,
      updatedPassword: cleanPassword,
      role: roleInput,
      createdAt: Date.now(),
      ...extraData
    };

    await setDoc(doc(db, "users", cleanEmail), userPayload, { merge: true });
    if (firebaseUid && firebaseUid !== cleanEmail) {
      await setDoc(doc(db, "users", firebaseUid), userPayload, { merge: true }).catch(() => {});
    }

    const activeUser = { 
      uid: firebaseUid, 
      email: cleanEmail,
      displayName: cleanName,
      role: roleInput
    };
    setUser(activeUser);
    localStorage.setItem('gymghost_active_user', JSON.stringify(activeUser));
    return activeUser;
  };

  const loginWithGoogle = async (signUpRole = 'client') => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    let result;
    try {
      result = await signInWithPopup(auth, provider);
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        return null;
      }
      if (err.code === 'auth/configuration-not-found') {
        throw new Error("⚠️ Dostawca logowania Google nie jest jeszcze włączony w Konsoli Firebase!\n\nProszę wejść w console.firebase.google.com -> Authentication -> Sign-in method i włączyć Google.");
      }
      throw err;
    }

    const googleUser = result.user;
    const cleanEmail = (googleUser.email || "").toLowerCase().trim();
    const cleanUid = googleUser.uid;
    const displayName = googleUser.displayName || cleanEmail.split('@')[0];
    const photoURL = googleUser.photoURL || null;

    if (!cleanEmail) {
      throw new Error("⚠️ Nie udało się pobrać adresu e-mail z konta Google.");
    }

    let existingProfile = null;
    try {
      const snap1 = await getDoc(doc(db, 'users', cleanEmail)).catch(() => null);
      if (snap1 && snap1.exists()) {
        existingProfile = snap1.data();
      } else {
        const snap2 = await getDoc(doc(db, 'users', cleanUid)).catch(() => null);
        if (snap2 && snap2.exists()) {
          existingProfile = snap2.data();
        }
      }
    } catch (err) {
      console.warn("Google login Firestore check note:", err.message);
    }

    let role = existingProfile?.role || signUpRole || 'client';
    if (cleanEmail === "mateuszmikulski72@gmail.com") {
      role = "admin";
    }

    const userPayload = {
      id: cleanEmail,
      uid: cleanUid,
      email: cleanEmail,
      name: existingProfile?.name || displayName,
      displayName: existingProfile?.displayName || displayName,
      avatarUrl: existingProfile?.avatarUrl || photoURL,
      role: role,
      updatedAt: new Date().toISOString()
    };

    if (!existingProfile) {
      userPayload.createdAt = Date.now();
    }

    await setDoc(doc(db, "users", cleanEmail), userPayload, { merge: true }).catch(() => {});
    if (cleanUid && cleanUid !== cleanEmail) {
      await setDoc(doc(db, "users", cleanUid), userPayload, { merge: true }).catch(() => {});
    }

    const activeUser = {
      uid: cleanUid,
      email: cleanEmail,
      displayName: userPayload.displayName,
      avatarUrl: photoURL,
      role: role
    };

    setUser(activeUser);
    return activeUser;
  };

  const resendVerificationEmail = async () => {
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        return true;
      }
    } catch (err) {
      console.warn("Verification email notice:", err.message);
    }
    return false;
  };

  const checkEmailVerificationStatus = async () => {
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          setUser((prev) => ({ ...prev, emailVerified: true }));
          const stored = localStorage.getItem('gymghost_active_user');
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              parsed.emailVerified = true;
              localStorage.setItem('gymghost_active_user', JSON.stringify(parsed));
            } catch (e) {}
          }
          return true;
        }
      }
    } catch (err) {
      console.warn("Check verification status error:", err.message);
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, syncError, verifyLocalSync, forceTokenRefresh, login, signup, loginWithGoogle, logout, resendVerificationEmail, checkEmailVerificationStatus }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

